import { useQuery, type UseQueryResult } from "@tanstack/react-query";
import { useAuth } from "@bw/shared/hooks";
import { ownerContractApi } from "@bw/shared/api";
import type {
  ContractTermsResponse,
  OrgContractVersionOwnerView,
  OrgContractAcceptanceOwnerView,
} from "@bw/shared/types/generated";

export interface OwnerContractQueries {
  termsQuery: UseQueryResult<ContractTermsResponse, Error>;
  versionsQuery: UseQueryResult<OrgContractVersionOwnerView[], Error>;
  acceptancesQuery: UseQueryResult<OrgContractAcceptanceOwnerView[], Error>;
  refresh: () => Promise<void>;
}

/** Actor/org-scoped safe reads; no acceptance derived from historical receipts. */
export function useOwnerContract(
  orgId: string | undefined,
): OwnerContractQueries {
  const { user, loading } = useAuth();
  const key = ["owner-contract", user?.uid, orgId];
  const enabled = !loading && Boolean(user?.uid && orgId);
  const termsQuery = useQuery({
    queryKey: [...key, "terms"],
    queryFn: () => ownerContractApi.getTerms(orgId!),
    enabled,
    retry: false,
  });
  const versionsQuery = useQuery({
    queryKey: [...key, "versions"],
    queryFn: () => ownerContractApi.listVersions(orgId!),
    enabled,
    retry: false,
  });
  const acceptancesQuery = useQuery({
    queryKey: [...key, "acceptances"],
    queryFn: () => ownerContractApi.listAcceptances(orgId!),
    enabled,
    retry: false,
  });
  return {
    termsQuery,
    versionsQuery,
    acceptancesQuery,
    refresh: async () => {
      await Promise.all([
        termsQuery.refetch(),
        versionsQuery.refetch(),
        acceptancesQuery.refetch(),
      ]);
    },
  };
}
